<template>

  <div class="page-wrapper">
    <div class="stepper-header">
      <v-img alt="Step Logo" class="step5-image step-logo"/>
      <span class="reg-titre1" v-html="title"></span>
    </div>

    <div>
      <form>
        <div style="margin-left: -10px">
          <v-radio-group v-model="loginMode" class="radio-group" @update:model-value="handleLoginMode">
            <v-radio :label="modeConnexion2" :value="1"></v-radio>
            <v-radio :label="modeConnexion1" :value="2"></v-radio>

            <div v-if="loginMode == 2" class="password-wrapper">
              <div class="password-section">

                <div class="password-inputs">
                  <SectionPassword
                      ref="PASSWORD"
                      :hide-button="true"
                      class="custom-fieldS5"
                      @check-password="checkPassword"
                  />
                </div>

                <div class="password-feedback">
                  <v-progress-linear
                      :model-value="passwordScore"
                      :color="passwordColor"
                      height="8"
                      class="mb-4"
                      rounded/>

                  <ul class="password-status">
                    <li :class="{ invalid: !rules.length }">
                      <v-icon  :color="rules.length ? '#5F8910' : '#C2470A'" size="12" class="mr-1">fa-solid fa-check</v-icon>
                      Au moins 6 caractères
                    </li>

                    <li :class="{ invalid: !rules.upper }">
                      <v-icon :color="rules.upper ? '#5F8910' : '#C2470A'" size="12" class="mr-1">fa-solid fa-check</v-icon>
                      1 lettre majuscule
                    </li>

                    <li :class="{ invalid: !rules.number }">
                      <v-icon :color="rules.number ? '#5F8910' : '#C2470A'" size="12" class="mr-1">fa-solid fa-check</v-icon>
                      1 chiffre
                    </li>

                    <li :class="{ invalid: !rules.special }">
                      <v-icon :color="rules.special ? '#5F8910' : '#C2470A'" size="12" class="mr-1">fa-solid fa-check</v-icon>
                      1 caractère spécial
                    </li>
                  </ul>
                </div>

              </div>
            </div>

          </v-radio-group>
        </div>
      </form>
    </div>
  </div>

  <div class="action-footer">
    <loader-button
        :label="precedentBtnTxt"
        :outlined="true"
        @callback="previous">
    </loader-button>
    <loader-button
        :label="suivantBtnTxt"
        :loading="loading"
        @callback="next">
    </loader-button>
  </div>

</template>

<script lang="ts">
import {ConnectionParamModes, LoginAnswer, LoginParam} from '@/generated/classes/Session';
import {
  AppButtons,
  AppCustoms,
  FwkControls,
  RessourceAppButton,
  RessourceAppCustom,
  RessourceFwkControl,
} from '@/ressources/globalRessources';
import User from '@/generated/classes/User';
import SectionPassword from "@/components/Register/SectionPassword.vue";
import {defineComponent, type PropType} from "vue";
import LoaderButton from "@/framework/base/components/LoaderButton.vue";
import SiretInscription, {SiretInscriptionAnswser} from "@/generated/classes/SiretInscription.ts";
import {storeSession} from "@/generated/stores";
import {useRoute, useRouter} from "vue-router";
import {Alerts} from "@/framework/base/helpers/Alerts";
import Dialog from "@/generated/classes/Dialog";

export default defineComponent({
  components: {
    LoaderButton,
    SectionPassword
  },
  props: {
    user: {
      type: Object as PropType<User>,
      required: true
    },
    subscription: {
      type: Object as PropType<SiretInscription>,
      required: true
    },
  },
  data() {
    return {
      loginMode: 1,
      modeConnexion1: RessourceFwkControl(FwkControls.MODE_CONNEXION_PASSWORD).label,
      modeConnexion2: RessourceFwkControl(FwkControls.MODE_CONNEXION_CODE_MAIL).label,
      passwordText: '',
      loading: false,
      registrationComplete: false,
      registrationDialog: null as Dialog | null,
      router: useRouter(),
      route: useRoute(),
      rules: {
        length: false,
        upper: false,
        number: false,
        special: false
      },
    };
  },
  computed: {
    title(): string {
      return RessourceAppCustom(AppCustoms.REGISTER_ETAPE5_LABEL).label;
    },
    suivantBtnTxt(): string {
      return RessourceAppButton(AppButtons.CREER_COMPTE).label;
    },
    precedentBtnTxt(): string {
      return RessourceAppButton(AppButtons.PRECEDENT).label;
    },
    hasPassword(): boolean {
      return this.user.hasPassword;
    },
    passwordScore(): number {
      return Object.values(this.rules).filter(Boolean).length * 25;
    },
    passwordColor(): string {
      const score = this.passwordScore;
      if (score == 100) return "#7EB715";
      return "#C2470A";
    }
  },

  methods: {
    checkPassword(value: string) {
      this.passwordText = value;
      this.rules.length = value.length >= 8;
      this.rules.upper = /[A-Z]/.test(value);
      this.rules.number = /[0-9]/.test(value);
      this.rules.special = /[^A-Za-z0-9]/.test(value);
      this.subscription.user.hasPassword = true;
      this.subscription.password = value;
    },
    handleLoginMode(value: number): void {
      this.subscription.user.connectionMode = value;
    },
    isPasswordValid(): boolean {
      return this.$refs.PASSWORD.passwordCorrect();
    },
    checkFields(): boolean {
      return this.$refs.PASSWORD.checkFields();
    },
    previous() {
      this.$emit('previous');
    },
    next() {
      if (this.loginMode == ConnectionParamModes.PASSWORD && this.checkFields() && this.isPasswordValid()) {
        this.subscribeSiret();
      } else if (this.loginMode == ConnectionParamModes.CODE) {
        this.subscribeSiret();
      }
    },

    async subscribeSiret(): Promise<void> {
      if (this.loading) return;
      if (this.registrationDialog != null) {
        await Alerts.message(this.registrationDialog);
        return;
      }
      this.loading = true;
      try {
        if (!this.registrationComplete) {
          const answer: SiretInscriptionAnswser = await SiretInscription.subscribeSiret(this.subscription);
          if (!answer.success) return;
          this.registrationComplete = true;
          if (answer.dialog != null) {
            this.registrationDialog = answer.dialog;
            await storeSession.logout();
            await Alerts.message(answer.dialog);
            return;
          }
        }

        await storeSession.logout();
        const param = new LoginParam();
        param.identifiant = this.subscription.user.mail;
        param.codeConnection = this.subscription.code.replace(' ', '');
        param.password = this.subscription.password;
        const answer: LoginAnswer = await storeSession.login(param);
        if (answer.isDomtom) {
          const dialog = new Dialog();
          dialog.label = answer.html;
          await Alerts.message(dialog);
        } else if (answer.user != null && this.route.name != 'Dashboard') {
          await this.router.push({name: 'Dashboard'});
        }
      } catch {
        // HTTP errors are displayed by the shared request handler.
      } finally {
        this.loading = false;
      }
    },
  },
})

</script>

<style>
.password div.v-messages {
  display: block !important;
}
.password-wrapper {
  max-width: 100%;
  margin-top: 12px;
  margin-left: 10px;
}
.custom-fieldS5 .v-field input{
  background-color: #F7F8F8 !important;
  border-radius: 8px !important;
}

.password-section {
  display: flex;
  gap: 32px;
  align-items: flex-start;
}

.password-inputs {
  flex: 1;
  max-width: 300px;
}

.password-feedback {
  margin-top: 30px;
  width: 190px;
}

.password-status {
  list-style: none;
  padding-left: 0;
  font-size: 14px;
}

.password-status li {
  margin-bottom: 6px;
  color: #5F8910;
}

.password-status li.invalid {
  color: #C2470A;
}

</style>
